import React, { useState, useEffect, useRef } from "react";
import "./App.scss";
import About from "./Components/About";
import Home from "./Components/Home";
import Services from "./Components/Services";
import Contact from "./Components/Contact";
import ScrollToTop from "./Components/ScrollToTop";
import ProductDetail from "./Components/ProductDetail";
import ProductList from "./Components/ProductList";
import { products } from "./data/productsData";

import {
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { FaInstagram, FaWhatsapp, FaChevronDown } from "react-icons/fa";

// ──────────────────────────────────────────────
//  Navigation component (needs Router context)
// ──────────────────────────────────────────────
function NavBar({ handleConversion }) {
  const [isVisible, setIsVisible] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const megaMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isProductPage =
    location.pathname.startsWith("/products") ||
    location.pathname === "/products";

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > 50) setIsScrolled(true);
      else setIsScrolled(false);
      if (currentScrollY > lastScrollY && currentScrollY > 50) setIsVisible(false);
      else setIsVisible(true);
      setLastScrollY(currentScrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  // Close mega menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (megaMenuRef.current && !megaMenuRef.current.contains(e.target)) {
        setMegaMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mega menu on route change
  useEffect(() => {
    setMegaMenuOpen(false);
  }, [location]);

  const handleHomeNav = (hash) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      className={`nav nav--left-logo ${isScrolled || isProductPage ? "scrolled" : ""
        } ${!isVisible ? "hidden" : ""}`}
    >
      {/* Logo – always left */}
      <div className="nav__logo">
        <Link to="/">
          <img src="/images/sns-logo.webp" alt="S.N.S. Glass House" />
        </Link>
      </div>

      <ul className="nav-links-list">
        {/* HOME */}
        <li>
          <button
            className="links nav-btn"
            onClick={() => handleHomeNav("#home")}
            id="nav-home"
          >
            HOME
          </button>
        </li>

        {/* ── MEGA MENU – PRODUCTS ── */}
        <li
          className="mega-menu-li"
          ref={megaMenuRef}
          onPointerEnter={(e) => { if (e.pointerType === 'mouse') setMegaMenuOpen(true); }}
          onPointerLeave={(e) => { if (e.pointerType === 'mouse') setMegaMenuOpen(false); }}
        >
          <button
            className={`links nav-btn mega-trigger ${megaMenuOpen ? "active" : ""}`}
            onClick={() => setMegaMenuOpen((v) => !v)}
            aria-haspopup="true"
            aria-expanded={megaMenuOpen}
            id="nav-products"
          >
            PRODUCTS <FaChevronDown className={`mega-chevron ${megaMenuOpen ? "rotate" : ""}`} />
          </button>

          {/* Dropdown Panel */}
          <div className={`mega-menu ${megaMenuOpen ? "mega-menu--open" : ""}`}>
            <div className="mega-menu__header">
              <span className="mega-menu__title">Product Catalogue</span>
              <Link to="/products" className="mega-menu__see-all" onClick={() => setMegaMenuOpen(false)}>
                See All →
              </Link>
            </div>
            <ul className="mega-menu__list">
              {products.map((p, i) => (
                <li key={p.id}>
                  <Link
                    to={`/products/${p.slug}`}
                    className="mega-menu__item"
                    id={`mega-item-${p.id}`}
                    onClick={() => setMegaMenuOpen(false)}
                  >
                    <span className="mega-menu__item-num">{String(i + 1).padStart(2, "0")}</span>
                    <span className="mega-menu__item-label">{p.navLabel}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </li>

        {/* ABOUT */}
        <li>
          <button
            className="links nav-btn"
            onClick={() => handleHomeNav("#about")}
            id="nav-about"
          >
            ABOUT
          </button>
        </li>

        {/* CONTACT */}
        <li>
          <button
            className="links nav-btn"
            onClick={() => handleHomeNav("#contact")}
            id="nav-contact"
          >
            CONTACT
          </button>
        </li>
      </ul>
    </nav>
  );
}

// ──────────────────────────────────────────────
//  Main App
// ──────────────────────────────────────────────
function App() {
  const handleConversion = () => {
    if (typeof window.gtag !== "undefined") {
      window.gtag("event", "contact_interaction", {
        event_category: "engagement",
        event_label: "contact_click",
        value: 1,
      });
    }
  };

  return (
    <div className="app" id="root-app">
      <NavBar handleConversion={handleConversion} />
      <ScrollToTop />

      <Routes>
        {/* Home page */}
        <Route
          path="/"
          element={
            <>
              <Home />
              <Services />
              <About />
              <Contact handleConversion={handleConversion} />
              <footer className="footer top-border">
                <div className="footer__logo">
                  <img src="/images/sns-logo.webp" alt="S.N.S. Glass House" />
                </div>
                <div className="social-links">
                  <span>
                    <a
                      href="https://instagram.com/snsglasshouse?igshid=YmMyMTA2M2Y="
                      className="flex-center"
                    >
                      <FaInstagram /> INSTAGRAM
                    </a>
                  </span>
                  <span>
                    <a
                      href="https://wa.me/919716043891"
                      onClick={handleConversion}
                      className="flex-center"
                    >
                      <FaWhatsapp /> WHATSAPP
                    </a>
                  </span>
                </div>
              </footer>
            </>
          }
        />

        {/* Product catalogue listing */}
        <Route path="/products" element={<>
          <ProductList />
          <footer className="footer top-border">
            <div className="footer__logo">
              <img src="/images/sns-logo.webp" alt="S.N.S. Glass House" />
            </div>
            <div className="social-links">
              <span>
                <a href="https://instagram.com/snsglasshouse?igshid=YmMyMTA2M2Y=" className="flex-center">
                  <FaInstagram /> INSTAGRAM
                </a>
              </span>
              <span>
                <a href="https://wa.me/919716043891" onClick={handleConversion} className="flex-center">
                  <FaWhatsapp /> WHATSAPP
                </a>
              </span>
            </div>
          </footer>
        </>} />

        {/* Individual product detail */}
        <Route path="/products/:slug" element={<>
          <ProductDetail />
          <footer className="footer top-border">
            <div className="footer__logo">
              <img src="/images/sns-logo.webp" alt="S.N.S. Glass House" />
            </div>
            <div className="social-links">
              <span>
                <a href="https://instagram.com/snsglasshouse?igshid=YmMyMTA2M2Y=" className="flex-center">
                  <FaInstagram /> INSTAGRAM
                </a>
              </span>
              <span>
                <a href="https://wa.me/919716043891" onClick={handleConversion} className="flex-center">
                  <FaWhatsapp /> WHATSAPP
                </a>
              </span>
            </div>
          </footer>
        </>} />
      </Routes>
    </div>
  );
}

export default App;
